import { useState } from 'react';
import { CHORDS, COURSE } from '@gc/shared';
import { strum } from '../lib/audio.js';
import { haptic } from '../lib/telegram.js';

const X0 = 16;
const DX = 20;
const Y0 = 40;
const DY = 25;

/**
 * Схема аккорда в привычном виде: струны — вертикальные линии, лады —
 * горизонтальные, цифра в точке это номер пальца. × над струной значит
 * «не играем её», ○ — «звучит открытой».
 */
export function ChordDiagram({ name, lit }: { name: string; lit: number[] }): JSX.Element {
  const shape = CHORDS[name]!;
  const width = X0 * 2 + DX * 5;
  const height = Y0 + DY * 4 + 16;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="chord-svg" role="img" aria-label={`Аккорд ${name}`}>
      {/* Верхний порожек: все аккорды курса — в первой позиции. */}
      <line x1={X0} y1={Y0} x2={X0 + DX * 5} y2={Y0} className="nut" />
      {[1, 2, 3, 4].map((f) => (
        <line key={f} x1={X0} y1={Y0 + DY * f} x2={X0 + DX * 5} y2={Y0 + DY * f} className="fret" />
      ))}
      {shape.frets.map((fret, i) => {
        const x = X0 + DX * i;
        const cy = Y0 + DY * (fret - 0.5);
        return (
          <g key={i}>
            <line
              x1={x}
              y1={Y0}
              x2={x}
              y2={Y0 + DY * 4}
              className={`string ${lit.includes(i) ? 'lit' : ''}`}
              strokeWidth={1 + i * 0.25}
            />
            <text x={x} y={Y0 - 9} className="mark" textAnchor="middle">
              {fret < 0 ? '×' : fret === 0 ? '○' : ''}
            </text>
            {fret > 0 && (
              <>
                <circle cx={x} cy={cy} r={8.5} className="dot" />
                <text x={x} y={cy + 3.2} className="dot-num" textAnchor="middle">
                  {shape.fingers[i] || ''}
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export function ChordCard({ name, active }: { name: string; active?: boolean }): JSX.Element {
  const [lit, setLit] = useState<number[]>([]);
  const hint = COURSE.chords[name]?.hint ?? '';

  function play(): void {
    haptic.tap();
    strum(name);
    // Подсветка идёт за звуком: струны загораются в том же порядке,
    // в каком их задевает рука.
    const shape = CHORDS[name]!;
    let k = 0;
    shape.frets.forEach((fret, i) => {
      if (fret < 0) return;
      const at = k * 26;
      k++;
      window.setTimeout(() => setLit((prev) => [...prev, i]), at);
      window.setTimeout(() => setLit((prev) => prev.filter((s) => s !== i)), at + 420);
    });
  }

  return (
    <button className={`chord ${active ? 'on' : ''}`} onClick={play} type="button">
      <span className="chord-name">{name}</span>
      <ChordDiagram name={name} lit={lit} />
      <span className="chord-hint">{hint}</span>
      <span className="chord-play">▶ звук</span>
    </button>
  );
}
